var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var Observable_1;
import { uuid } from '../../Helpers/Uuid/Uuid';
import { hasDiff } from '../../Helpers/Difference/Difference';
import { injectable } from '../Decorators/Injectable';
let Observable = Observable_1 = class Observable {
    get value() {
        return this._value;
    }
    static combine(consumer, observables) {
        const initValue = observables.map(observable => observable.value);
        const observable = new Observable_1(initValue);
        observables.forEach((obs, i) => {
            obs.observe(consumer, value => {
                const update = [...observable.value];
                update[i] = value;
                observable.next(update);
            });
        });
        return observable;
    }
    constructor(initValue, debounceTime = 0) {
        this._value = initValue;
        this.debounceTime = debounceTime;
        this._subs = new Map();
        this.pipes = [];
    }
    when(fn) {
        let sub = undefined;
        const promise = new Promise((res) => {
            sub = this.subscribe(value => {
                Promise.resolve()
                    .then(() => fn(value))
                    .then(isOK => {
                    if (isOK) {
                        res(value);
                        sub === null || sub === void 0 ? void 0 : sub.unsubscribe();
                    }
                });
            });
        });
        promise.cancel = () => {
            sub === null || sub === void 0 ? void 0 : sub.unsubscribe();
        };
        return promise;
    }
    setValue(value) {
        this._value = this.runPipes(value);
        return this.value;
    }
    debounce(time) {
        this.debounceTime = time;
        return this;
    }
    pipe(fn) {
        this.pipes.push(fn);
        return this;
    }
    runPipes(value) {
        return this.pipes.reduce((acc, fn) => {
            return fn(acc);
        }, value);
    }
    next(value) {
        this.setValue(value);
        this.debounceUpdate();
        return this;
    }
    update(value) {
        return this.next(Object.assign(Object.assign({}, this.value), value));
    }
    debounceUpdate() {
        if (this.debounceTime > 0) {
            if (!this.timeout) {
                this.timeout = setTimeout(() => {
                    this.submit();
                    this.timeout = null;
                }, this.debounceTime);
            }
            return this;
        }
        this.submit();
        return this;
    }
    submit() {
        this._subs.forEach(sub => {
            const updatedValue = sub.pipes.reduce((acc, fn) => {
                return fn(acc);
            }, this._value);
            sub.subscriptions.forEach(fn => {
                if (sub.rules.find(fn => fn(this.value) === false)) {
                    return;
                }
                fn(updatedValue);
            });
        });
        return this;
    }
    removeObserver(consumer) {
        this._subs.delete(consumer);
    }
    observe(consumer, fn) {
        if (this._subs.has(consumer)) {
            const sub = this._subs.get(consumer);
            const stringFn = fn.toString();
            if (!sub.subscriptions.find(subFn => subFn.toString() === stringFn)) {
                sub.subscriptions.push(fn);
                if (this.value !== undefined) {
                    this.debounceUpdate();
                }
                this._subs.set(consumer, sub);
            }
            return sub;
        }
        const sub = {
            id: consumer,
            subscriptions: [fn],
            rules: [],
            pipes: [],
            pipe(fn) {
                this.pipes.push(fn);
                return this;
            },
            if(fn) {
                this.rules.push(fn);
                return this;
            },
            unsubscribe: () => {
                this.removeObserver(consumer);
            },
            removeSignature(fn) {
                const stringFn = fn.toString();
                this.subscriptions = this.subscriptions.filter(subFn => subFn.toString() !== stringFn);
                return this;
            }
        };
        if (consumer && typeof consumer === 'object') {
            if (consumer.isReactComponent) {
                const componentWillUnmount = (consumer.componentWillUnmount || (() => { })).bind(consumer);
                consumer.componentWillUnmount = () => {
                    componentWillUnmount();
                    sub.unsubscribe();
                };
            }
            else {
                const onDestroy = (consumer.onDestroy || (() => { })).bind(consumer);
                consumer.onDestroy = () => {
                    onDestroy();
                    sub.unsubscribe();
                };
            }
        }
        this._subs.set(consumer, sub);
        if (this.value !== undefined) {
            if (sub.rules.find(fn => fn(this.value) === false)) {
                return sub;
            }
            if (this.timeout) {
                return sub;
            }
            else if (this.debounceTime > 0) {
                this.debounceUpdate();
                return sub;
            }
            fn(this.value);
        }
        return sub;
    }
    subscribe(fn) {
        const id = Observable_1.uuid();
        return this.observe(id, fn);
    }
};
Observable.uuid = uuid;
Observable.hasDiff = hasDiff;
Observable = Observable_1 = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object, Number])
], Observable);
export { Observable };
